import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { combineLatest, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { debounceTime, filter, finalize, switchMap, take } from 'rxjs/operators';
import { fadeAnimation, showAnimation } from 'src/animations';
import { Address } from 'src/app/lib/location/classes';
import { SidenavOption } from 'src/app/lib/shared/classes';
import { CompleteRequestDialogComponent, CompleteRequestDialogComponentInputData } from 'src/app/lib/shared/components/dialog/complete-request-dialog/complete-request-dialog.component';
import { DoubleCheckDialogComponent, DoubleCheckDialogComponentInputData } from 'src/app/lib/shared/components/dialog/double-check-dialog/double-check-dialog.component';
import { HereMapDialogComponent, HereMapDialogComponentData } from 'src/app/lib/shared/components/dialog/here-map-dialog/here-map-dialog.component';
import { CONCURRENCY_PROVIDER_SERVICE } from 'src/app/lib/shared/interfaces';
import { SessionService } from 'src/app/lib/shared/services/session.service';
import { SidenavService } from 'src/app/lib/shared/services/sidenav.service';
import { Contact, ContactInformation, Customer } from '../../../classes';
import { CONTACT_PROVIDER_SERVICE, CUSTOMERS_PROVIDER_SERVICE, DEPARTMENTS_PROVIDER_SERVICE } from '../../../interfaces';
import { ContactService } from '../../../services/contact.service';
import { CreateCustomerDialogComponent } from '../../dialog/create-customer-dialog/create-customer-dialog.component';
import { SelectCustomerDialogComponent } from '../../dialog/select-customer-dialog/select-customer-dialog.component';
import { ContactDetailViewComponentService } from './contact-detail-view-component-service';

@Component({
  selector: 'app-contact-detail-view',
  templateUrl: './contact-detail-view.component.html',
  styleUrls: ['./contact-detail-view.component.css'],
  providers: [
    { provide: CONTACT_PROVIDER_SERVICE, useClass: ContactDetailViewComponentService },
    { provide: CONCURRENCY_PROVIDER_SERVICE, useExisting: CONTACT_PROVIDER_SERVICE },
    { provide: CUSTOMERS_PROVIDER_SERVICE, useExisting: CONTACT_PROVIDER_SERVICE },
    { provide: DEPARTMENTS_PROVIDER_SERVICE, useExisting: CONTACT_PROVIDER_SERVICE },
    SidenavService
  ],
  animations: [showAnimation, fadeAnimation]
})
export class ContactDetailViewComponent implements OnDestroy, OnInit {

  appearance: 'fill' | 'outline' | 'standard' = 'fill';

  disabled: boolean = false;

  reset: boolean = false;

  contactInformationRefreshing: boolean = false;

  formGroup: FormGroup;

  sidenavOptions = {
    'editMode': {
      'icon': () => this.disabled ? 'edit' : 'edit_off',
      'title': () => this.disabled ? 'Bearbeiten' : 'Sperren',
      'onClick': () => of(this.toggleEditMode()),
      'visible': () => true,
      'appearance': () => 'default'
    } as SidenavOption,
    'save': {
      'icon': () => this.formGroup.dirty ? 'save' : 'done',
      'title': () => this.formGroup.dirty ? 'Speichern' : 'Aktuell',
      'onClick': () => this.formGroup.dirty ? this.saveChanges() : of(null),
      'visible': () => !this.disabled,
      'appearance': () => this.formGroup.dirty ? 'success' : 'ok'
    } as SidenavOption,
    'discard': {
      'icon': () => 'clear',
      'title': () => 'Verwerfen',
      'onClick': () => this.discardChanges(),
      'visible': () => !this.disabled && this.formGroup.dirty,
      'appearance': () => 'danger'
    } as SidenavOption,
    'watched_status': {
      'icon': () => this.formGroup.controls['isWatched'].value ? 'visibility' : 'visibility_off',
      'title': () => this.formGroup.controls['isWatched'].value ? 'Beobachtet' : 'Nicht beob.',
      'onClick': () => this.toggleWatchedStatus(),
      'visible': () => true,
      'appearance': () => 'dark'
    } as SidenavOption,
    'delete': {
      'icon': () => 'delete_forever',
      'title': () => 'Löschen',
      'onClick': () => this.deleteContact(),
      'visible': () => !this.disabled && this.rightsMap.delete_contact_personal,
      'appearance': () => 'danger'
    } as SidenavOption
  };

  rightsMap = {
    "delete_contact_personal": false
  };

  private _subscriptions: Subscription[] = [];

  constructor(
    @Inject(CONTACT_PROVIDER_SERVICE) public contactDetailViewComponentService: ContactDetailViewComponentService,
    private _dialog: MatDialog,
    private _formBuilder: FormBuilder,
    private _sidenavService: SidenavService,
    private _snackBar: MatSnackBar,
    private _sessionService: SessionService,
    private _contactService: ContactService
  ) { }

  addContactInformation = () => this.contactInformationControl.push(this._formBuilder.group(new ContactInformation()));

  afterStoring() {
    this.formGroup.markAsUntouched();
    this.formGroup.markAsPristine();
    this.contactDetailViewComponentService.refreshCurrentContact();
    this._snackBar.open('Änderungen erfolgreich gespeichert!', 'Speichern', {
      duration: 2000
    });
  }

  createCustomer() {
    this._dialog.open(CreateCustomerDialogComponent, {
      panelClass: "no-padding-dialog-container",
      autoFocus: false,
      data: { preventNavigation: true }
    }).afterClosed().subscribe((customer: Customer) => {
      if (customer) this.contactDetailViewComponentService.linkToCustomer(customer).toPromise();
    });
  }

  deleteContact() {
    let subject = new Subject();
    this.contactDetailViewComponentService.contact$.pipe(take(1)).subscribe((contact: Contact) => {
      var number = contact.contactperspublicid ? contact.contactperspublicid : Math.floor(1000000 + Math.random() * 9000000).toFixed(0);
      this._dialog.open(DoubleCheckDialogComponent, {
        autoFocus: false,
        panelClass: 'no-padding-dialog-container',
        data: {
          mode: 'numeric',
          title: 'Löschen bestätigen',
          text: `Bitte geben Sie die Nummer ${number} ein um den Ansprechpartner dauerhaft zu löschen`,
          doubleCheckValue: number
        } as DoubleCheckDialogComponentInputData
      }).afterClosed().subscribe((result) => {
        if (result) {
          this.contactDetailViewComponentService.deleteCurrentContact().subscribe(subject);
        } else subject.complete();
      });
    });
    return subject.asObservable();
  }

  discardChanges(): Observable<Object> {
    this.reset = true;
    return this.contactDetailViewComponentService.refreshCurrentContact();
  }

  initRights = (soft: boolean = true) => {
    for (let index in this.rightsMap) this._sessionService.hasRight(index, soft).subscribe(result => this.rightsMap[index] = result);
  }

  linkToCustomer() {
    combineLatest([this._dialog.open(SelectCustomerDialogComponent, {
      panelClass: 'no-padding-dialog-container'
    }).afterClosed(), this.contactDetailViewComponentService.customers$]).pipe(take(1)).subscribe(([customer, customers]) => {
      if (customer) {
        let index = customers.findIndex(x => x.customerid === customer.customerid);
        if (index > -1) {
          this._snackBar.open(`Der Kunde ist bereits zugeordnet`, 'Ok', { duration: 2000 });
          return;
        }
        this.contactDetailViewComponentService.linkToCustomer(customer).subscribe(() => {
          this.contactDetailViewComponentService.refreshCurrentCustomers();
          this._snackBar.open(`Der Kunde wurde erfolgreich zugeordnet`, 'Ok', { duration: 2000 });
        });
      }
    });
  }

  ngOnDestroy(): void {
    for (let sub of this._subscriptions) {
      sub.unsubscribe();
      this._subscriptions = [];
    }
    this.contactDetailViewComponentService.dispose();
  }

  ngOnInit(): void {

    this.formGroup = this._formBuilder.group({
      contact: this._formBuilder.group(new Contact()),
      address: this._formBuilder.group(new Address()),
      contactInformation: this._formBuilder.array([]),
      isWatched: this._formBuilder.control(false)
    });

    this._sidenavService.setOptions(Object.values(this.sidenavOptions));

    this._subscriptions.push(...[
      this.contactDetailViewComponentService.contact$.pipe(filter(x => this.formGroup.pristine || this.reset)).subscribe((contact: Contact) => this.setContact(contact)),
      combineLatest([this.contactInformationControl.valueChanges, this.contactDetailViewComponentService.contact$]).pipe(debounceTime(100)).subscribe(([value, contact]) => {
        if (this.formGroup.pristine && ((Object.values(this.contactInformationControl.controls).some(x => x.dirty)) || this.contactInformationControl.value.length !== (contact as Contact).contactinformation.length)) this.formGroup.markAsDirty();
      })
    ]);

    this.initRights();
  }

  resetContactInformation(contact: Contact) {
    this.contactInformationRefreshing = true;
    timer(100).subscribe(() => {
      this.contactInformationControl.clear();
      contact.contactinformation.forEach(x => {
        let group = this._formBuilder.group(x);
        this.contactInformationControl.push(group);
        if (this.disabled) group.disable();
      });
      this.contactInformationRefreshing = false;
    });
  }

  saveChanges() {
    let subject = new Subject();

    combineLatest([
      this._dialog.open(CompleteRequestDialogComponent, {
        panelClass: 'no-padding-dialog-container',
        data: {
          addNote: true
        } as CompleteRequestDialogComponentInputData
      }).afterClosed(),
      this.contactDetailViewComponentService.contact$
    ])
      .pipe(take(1))
      .subscribe(([output, contact]) => {
        if (output.abort) {
          subject.complete();
          return;
        }

        let formItemIds = (this.contactInformationControl.value as ContactInformation[]).map(x => x.infoid);
        this.contactDetailViewComponentService
          .saveChanges(
            this.formGroup.controls['contact'].value,
            output.note,
            this.formGroup.controls['address'].value,
            Object.values(this.contactInformationControl.controls).filter(x => x.dirty || x.value.itemid === null).map(x => x.value),
            (contact as Contact).contactinformation.map(x => x.infoid).filter(x => formItemIds.indexOf(x) === -1)
          )
          .pipe(finalize(() => this.afterStoring()))
          .subscribe(subject);
      });

    return subject.asObservable();
  }

  setContact(contact: Contact) {
    this.reset = false;
    this.formGroup.controls['contact'].patchValue(contact);
    this.formGroup.controls['address'].patchValue(contact.address ? contact.address : new Address());
    this.resetContactInformation(contact);
    this.formGroup.markAsPristine();
    timer(100).pipe(switchMap(() => this._contactService.contactIsWatched(contact.contactpersid))).subscribe((result: boolean) => this.formGroup.controls['isWatched'].setValue(result));
    this._contactService.saveHistory(contact.contactpersid);
  }

  showAddress = () => this._dialog.open(HereMapDialogComponent, {
    panelClass: 'no-padding-dialog-container',
    data: { address: this.formGroup.controls['address'].value } as HereMapDialogComponentData
  });

  toggleEditMode() {
    let pristine = this.formGroup.pristine;
    this.formGroup[this.disabled ? 'enable' : 'disable']({ emitEvent: false });
    this.disabled = !this.disabled;
    if (pristine) this.formGroup.markAsPristine();
  }

  toggleWatchedStatus() {
    let value = this.formGroup.controls['isWatched'].value ? true : false;
    this.contactDetailViewComponentService.contactId$.pipe(switchMap((contactId: number) => this._contactService.setContactWatchedStatus(contactId, !value)))
      .subscribe(() => this.formGroup.controls['isWatched'].setValue(!value));

    return of(null);
  }

  @HostListener('keydown', ['$event'])
  private _keydown(event: KeyboardEvent) {
    if (event.ctrlKey && event.key === 's') {
      event.preventDefault();
      event.stopPropagation();
      if (this.formGroup.dirty) this.saveChanges();
      else this._snackBar.open('Keine Änderungen zum Speichern erkannt', 'Ok', {
        duration: 2000
      });
    }
  }

  get addressControl(): AbstractControl {
    return this.formGroup.controls['address'];
  }
  get contactInformationControl(): FormArray {
    return this.formGroup.controls['contactInformation'] as FormArray;
  }

}
